import React, { FC, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import Alert from '@material-ui/lab/Alert';
import Snackbar from '@material-ui/core/Snackbar';
import Skeleton from '@material-ui/lab/Skeleton';
import { AlertDialog } from 'components/UI/Dialog/AlertDialog';
import Paper from '@material-ui/core/Paper';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import { Row } from 'components/UI/Row';
import { CustomerOrdersList } from 'features/OrdersList/CustomerOrdersList';
import {
  closeError,
  fetchCustomerDetails,
  saveCustomer,
  getCustomerDetailsApiErrors,
  getCustomerDetailsLoaders,
  getCustomerDetails
} from './customerForm';
import { CustomerFormProps, CustomerFormSchema } from './customerFormSchema';

const CustomerDetails: FC<{}> = ({}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { customerId } = useParams<{ customerId: string }>();
  const customer = useSelector(getCustomerDetails);
  const { isLoading, isSaving } = useSelector(getCustomerDetailsLoaders);
  const { loadingError, savingError } = useSelector(getCustomerDetailsApiErrors);
  const [openUnsavedAlert, setOpenUnsavedAlert] = useState(false);

  const { handleSubmit, errors, control, setValue, formState } = useForm<CustomerFormProps>({
    mode: 'onBlur',
    resolver: yupResolver(CustomerFormSchema)
  });
  const { isDirty } = formState;
  // TODO: make a custom hook
  useEffect(() => {
    if (customerId) {
      dispatch(fetchCustomerDetails({ customerId }));
    }
  }, [customerId, dispatch]);

  useEffect(() => {
    if (customer) {
      const { address, city, email, company_name, tax_code, zip_code, phone_number } = customer;
      setValue('email', email);
      setValue('company_name', company_name);
      setValue('tax_code', tax_code);
      setValue('address', address);
      setValue('city', city);
      setValue('zip_code', zip_code);
      setValue('phone_number', phone_number);
    }
  }, [customer, setValue]);

  const onSubmit = async (formData: CustomerFormProps) => {
    await dispatch(saveCustomer({ customer: formData, history, id: customer?.id }));
  };

  const cancelEdit = useCallback(() => {
    if (isDirty) {
      // TODO: put into redux for dealing with back on nav bar
      setOpenUnsavedAlert(true);
      return;
    }
    history.push('/customers');
  }, [isDirty, history]);

  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  return (
    <>
      <Paper elevation={3}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate style={{ padding: '15px' }}>
          <>
            {isLoading ? (
              <Skeleton variant="text" animation="wave" width="100%" height="60px" />
            ) : (
              <Controller
                as={
                  <TextField
                    autoComplete="new-password"
                    helperText={t(get(errors, 'company_name.message'))}
                    error={!isEmpty(get(errors, `company_name`))}
                    label={t('label.company name')}
                    fullWidth
                    style={{ marginBottom: '16px' }}
                    variant="outlined"
                  />
                }
                control={control}
                defaultValue=""
                name="company_name"
              />
            )}

            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
              <Grid item xs={12} sm={12} md={6}>
                {isLoading ? (
                  <Skeleton variant="text" animation="wave" width="100%" height="60px" />
                ) : (
                  <Controller
                    as={
                      <TextField
                        autoComplete="new-password"
                        helperText={t(get(errors, 'email.message'))}
                        error={!isEmpty(get(errors, `email`))}
                        label={t('label.email')}
                        fullWidth
                        type="email"
                        variant="outlined"
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="email"
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                {isLoading ? (
                  <Skeleton variant="text" animation="wave" width="100%" height="60px" />
                ) : (
                  <Controller
                    as={
                      <TextField
                        autoComplete="new-password"
                        helperText={t(get(errors, 'phone_number.message'))}
                        error={!isEmpty(get(errors, `phone_number`))}
                        fullWidth
                        label={t('label.phone number')}
                        type="default"
                        variant="outlined"
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="phone_number"
                  />
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
              <Grid item xs={12} sm={12} md={6}>
                {isLoading ? (
                  <Skeleton variant="text" animation="wave" width="100%" height="60px" />
                ) : (
                  <Controller
                    as={
                      <TextField
                        autoComplete="new-password"
                        helperText={t(get(errors, 'address.message'))}
                        error={!isEmpty(get(errors, `address`))}
                        fullWidth
                        label={t('label.address')}
                        type="default"
                        variant="outlined"
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="address"
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={4}>
                {isLoading ? (
                  <Skeleton variant="text" animation="wave" width="100%" height="60px" />
                ) : (
                  <Controller
                    as={
                      <TextField
                        autoComplete="new-password"
                        helperText={t(get(errors, 'city.message'))}
                        error={!isEmpty(get(errors, `city`))}
                        fullWidth
                        label={t('label.city')}
                        type="default"
                        variant="outlined"
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="city"
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={12} md={2}>
                {isLoading ? (
                  <Skeleton variant="text" animation="wave" width="100%" height="60px" />
                ) : (
                  <Controller
                    as={
                      <TextField
                        autoComplete="new-password"
                        helperText={t(get(errors, 'zip_code.message'))}
                        error={!isEmpty(get(errors, `zip_code`))}
                        fullWidth
                        label={t('label.zip code')}
                        type="default"
                        variant="outlined"
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="zip_code"
                  />
                )}
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginBottom: '16px' }}>
              <Grid item xs={12} sm={12} md={6}>
                {isLoading ? (
                  <Skeleton variant="text" animation="wave" width="100%" height="60px" />
                ) : (
                  <Controller
                    as={
                      <TextField
                        autoComplete="new-password"
                        helperText={t(get(errors, 'tax_code.message'))}
                        error={!isEmpty(get(errors, `tax_code`))}
                        fullWidth
                        label={t('label.tax_code')}
                        type="default"
                        variant="outlined"
                      />
                    }
                    control={control}
                    defaultValue=""
                    name="tax_code"
                  />
                )}
              </Grid>
            </Grid>
          </>

          <Row margin="20px 0 0" justifyContent="flex-end">
            <Button color="secondary" disabled={isSaving || isLoading} onClick={cancelEdit}>
              {t('label.cancel')}
            </Button>
            <Button type="submit" color="primary" disabled={isSaving || isLoading}>
              {t('label.save')}
              {isSaving && (
                <CircularProgress
                  color="primary"
                  size={34}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: -17,
                    marginLeft: -17
                  }}
                />
              )}
            </Button>
          </Row>
        </form>
      </Paper>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(savingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {savingError}
        </Alert>
      </Snackbar>
      <div style={{ height: '50px' }} />
      {customerId && <CustomerOrdersList />}
      <AlertDialog
        handleAccept={() => {
          setOpenUnsavedAlert(false);
          history.push('/customers');
        }}
        handleCancel={() => {
          setOpenUnsavedAlert(false);
        }}
        open={openUnsavedAlert}
        title={t('reportTemplates.unsaved changes title')}
        text={t('reportTemplates.unsaved changes text')}
      />
    </>
  );
};

export { CustomerDetails };
